import React from "react"
import { NavLink, Link } from "react-router-dom"
import clsx from "clsx"
import {
	useTable,
	useSortBy,
	usePagination,
	useFilters,
	useGlobalFilter,
	useRowSelect,
} from "react-table"
import { useTranslation } from "react-i18next"

import Emoji from "components/shared/Emoji"
import { useAuth } from "hooks/useAuth"
import { privateRoutes } from "router/routes"
import { operators } from "utils/constants/operators"
import IndeterminateCheckbox from "components/shared/IndeterminateCheckbox"
import { ExportData } from "./ExportData"

const PAGINATION_LENGTH = 10

export const PartnersTable = ({ data, isAdminOCode = false, className }) => {
	const { t } = useTranslation()
	const { user, isAdminOperator } = useAuth()

	// Columns definition
	const columns = React.useMemo(() => {
		const baseCols = [
			{
				Header: t("profil.client_number", { ns: "partner" }),
				accessor: "client_number",
				Cell: ({ value }) => (
					<Link
						className="c-link--page"
						to={{
							pathname: `${privateRoutes.partner.basePath}/${value}`,
						}}
					>
						{value}
					</Link>
				),
			},
			{
				Header: t("profil.social_reason", { ns: "partner" }),
				accessor: "social_reason",
			},
			{
				Header: t("profil.commercial_name", { ns: "partner" }),
				accessor: "commercial_name",
			},
			{
				accessor: "siret",
			},
			{
				accessor: "address",
			},
			{
				Header: t("profil.postal_code", { ns: "partner" }),
				accessor: "postal_code",
			},
			{
				Header: t("profil.town", { ns: "partner" }),
				accessor: "town",
			},
			{
				Header: t("profil.e-mail", { ns: "partner" }),
				accessor: "email",
			},
			{
				Header: t("owner.phonenumber", { ns: "bike" }),
				accessor: "phone",
			},
			{
				accessor: "nom_representant_legal",
			},
			{
				accessor: "prenom_representant_legal",
			},
			{
				accessor: "mfc_client_number",
			},
			{
				accessor: "create_date",
			},
			{
				accessor: "keywords",
			},
			{
				accessor: "gender_mfc",
			},
		]
		const adminCols = operators.map((op) => ({
			Header: op.name,
			accessor: (data) => (data?.operators?.includes(op.id_api) ? op.id_api : null),
			Cell: ({ value }) => <div className="text-center">{value}</div>,
		}))

		return [...baseCols, ...(isAdminOCode ? adminCols : [])]
	}, [isAdminOCode, t])

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		selectedFlatRows,
		page, // the rows for the active page

		// pagination utilities
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		setGlobalFilter,
		state: { selectedRowIds, pageIndex },
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: [
					"address",
					"siret",
					"nom_representant_legal",
					"prenom_representant_legal",
					"mfc_client_number",
					"create_date",
					"keywords",
					"gender_mfc",
				],
			},
		},
		useFilters,
		useGlobalFilter,
		useSortBy,
		usePagination,
		useRowSelect,

		(hooks) => {
			// Display checkbox only for admin users
			;(isAdminOCode || isAdminOperator) &&
				hooks.visibleColumns.push((columns) => [
					// Let's make a column for selection
					{
						id: "selection",
						// The header can use the table's getToggleAllRowsSelectedProps method
						// to render a checkbox
						Header: ({ getToggleAllRowsSelectedProps }) => (
							<IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
						),
						// The cell can use the individual row's getToggleRowSelectedProps method
						// to the render a checkbox
						Cell: ({ row }) => (
							<IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
						),
					},
					...columns,
				])
		}
	)

	const getColumnSize = React.useCallback((columnName) => {
		// sum should be 24
		switch (columnName) {
			default:
				return "flex-initial"
		}
	}, [])

	// Manage global search
	// -------------------
	const [dataTableFilter, setDataTableFilter] = React.useState("")
	React.useEffect(() => setGlobalFilter(dataTableFilter), [dataTableFilter, setGlobalFilter])

	// Manage pagination
	// -------------------
	const middleDistance = Math.round((PAGINATION_LENGTH - 1) / 2)
	const pageShouldBeHiddenInPagination = (index) => {
		// in the first pages,
		// pagination should not exceed PAGINATION_LENGTH
		const condition1 = pageIndex <= middleDistance && index > PAGINATION_LENGTH - 1
		// in the middle pages,
		// show x pages before currentPage and y pages after currentPage
		const condition2 =
			pageIndex > middleDistance &&
			pageIndex < pageOptions.length - middleDistance &&
			(index < pageIndex - middleDistance || index > pageIndex + middleDistance - 1)
		// in the last pages,
		// always show the x last pages
		const condition3 =
			pageIndex > pageOptions.length - 1 - middleDistance &&
			index < pageOptions.length - PAGINATION_LENGTH
		return condition1 || condition2 || condition3
	}

	return (
		<>
			<div className={"c-table-team " + (className ? className : "")}>
				<div className="c-table-team__header md:mb-0 pb-7">
					<div className="flex flex-col md:flex-row justify-between mb-7 md:mb-0">
						<div className="flex-1">
							{/* Global filter */}
							<div className={"c-form-group c-form-group--picto mb-7 md:mb-0"}>
								<label htmlFor="tableglobalFilter" className="c-label mb-1">
									{t("list.search", { ns: "partner" })}
								</label>
								<svg
									className="c-input-picto"
									xmlns="http://www.w3.org/2000/svg"
									height="20"
									width="20"
									viewBox="0 0 512 512"
								>
									<path d="M505.749 475.587l-145.6-145.6c28.203-34.837 45.184-79.104 45.184-127.317C405.333 90.926 314.41.003 202.666.003S0 90.925 0 202.669s90.923 202.667 202.667 202.667c48.213 0 92.48-16.981 127.317-45.184l145.6 145.6c4.16 4.16 9.621 6.251 15.083 6.251s10.923-2.091 15.083-6.251c8.341-8.341 8.341-21.824-.001-30.165zM202.667 362.669c-88.235 0-160-71.765-160-160s71.765-160 160-160 160 71.765 160 160-71.766 160-160 160z" />
								</svg>
								<input
									className={"c-input pl-12"}
									id={"tableglobalFilter"}
									name={"tableglobalFilter"}
									type={"text"}
									placeholder={`${t("store.contact_name", {
										ns: "partner",
									})}, ${t("store.structure_name", { ns: "partner" })}, ...`}
									value={dataTableFilter}
									style={{ maxWidth: "400px" }}
									onChange={(event) => setDataTableFilter(event.target.value)}
								/>
							</div>
						</div>
						{user?.permission !== 1 && (
							<div className="">
								<NavLink
									to="/ajouter-un-partenaire"
									className="c-btn c-btn--primary px-7"
								>
									{t("list.add", { ns: "partner" })}
								</NavLink>
							</div>
						)}
					</div>
				</div>

				<div
					className={clsx("c-grid-table pb-2 ", {
						"c-grid-table--admin": isAdminOCode,
						"c-grid-table--noadmin": !isAdminOCode && !isAdminOperator,
						"c-grid-table--admin-operator": isAdminOperator,
					})}
				>
					<table className="c-grid-table__wrapper" {...getTableProps()}>
						<thead>
							{headerGroups.map((headerGroup) => (
								<tr
									className="c-grid-table__head"
									{...headerGroup.getHeaderGroupProps()}
								>
									{headerGroup.headers.map((column) => {
										return (
											<th
												className={
													"c-table__cell flex p-5 uppercase " +
													getColumnSize(column?.id)
												}
												{...column.getHeaderProps(
													column.getSortByToggleProps()
												)}
											>
												<span className={"u-ellipsis"}>
													{column.render("Header")}
												</span>
												<span>
													{column.isSorted
														? column.isSortedDesc
															? " 🔽"
															: " 🔼"
														: ""}
												</span>
											</th>
										)
									})}
								</tr>
							))}
						</thead>

						<tbody {...getTableBodyProps()}>
							{page.map((row) => {
								// const hasAdminRights = user?.permission === -1 || user?.permission === 0 // admin or manager
								prepareRow(row)
								return (
									<tr
										className={
											"c-grid-table__line p-5 lg:p-0 " +
											(row.isSelected ? "selected" : "")
										}
										{...row.getRowProps()}
									>
										{row.cells.map((cell) => {
											return (
												<td
													className={
														"c-table__cell md:p-5 " +
														getColumnSize(cell?.column?.id)
													}
													{...cell.getCellProps()}
												>
													<span className={"u-ellipsis"}>
														{cell.render("Cell")}
													</span>
												</td>
											)
										})}
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>

				{/* Selection of partners to export in a excel file - Admin oCode option only */}
				{(isAdminOCode || isAdminOperator) && (
					<ExportData
						selectedDatas={selectedFlatRows}
						datas={data}
						disabled={Object.keys(selectedRowIds).length === 0}
						isAdminOCode={isAdminOCode}
					/>
				)}

				{data.length === 0 && (
					<p className="text-center mt-5">
						{t("table.no_result", { ns: "team" })}{" "}
						<Emoji label="visage triste" symbol="😟" />
					</p>
				)}

				{/* Pagination */}
				{pageOptions && pageOptions.length > 1 && (
					<div className="flex justify-center mt-5">
						<div className="c-pagination">
							<button
								className="c-btn c-btn--primary px-2 mx-5"
								onClick={() => gotoPage(0)}
								disabled={!canPreviousPage}
							>
								{"<<"}
							</button>
							{pageOptions.map((index) => {
								if (pageShouldBeHiddenInPagination(index))
									return <React.Fragment key={index}></React.Fragment>
								return (
									<button
										type="button"
										key={index}
										className={index === pageIndex ? "font-bold" : ""}
										style={{ width: "30px" }}
										onClick={() => gotoPage(index)}
									>
										{index + 1}
									</button>
								)
							})}
							<button
								className="c-btn c-btn--primary px-2 mx-5"
								onClick={() => gotoPage(pageCount - 1)}
								disabled={!canNextPage}
							>
								{">>"}
							</button>
						</div>
					</div>
				)}
			</div>
		</>
	)
}
